import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Overdue / Delayed Trip Alerts (CT-32)
 * Header pill + slide-in command panel + toast for new escalations.
 * Backend: GET /api/trip-alerts/:operatorId, POST /api/trip-alerts/action
 */

const SEV = {
    critical: { label: 'Critical', fg: '#b91c1c', bg: '#fef2f2', border: '#fecaca', solid: '#dc2626' },
    overdue: { label: 'Overdue', fg: '#c2410c', bg: '#fff7ed', border: '#fed7aa', solid: '#ea580c' },
    delayed: { label: 'Delayed', fg: '#a16207', bg: '#fefce8', border: '#fde68a', solid: '#d97706' },
};

const QUICK_NOTES = ['Driver on the way', 'No answer', 'Vehicle breakdown', 'Driver unwell', 'Traffic / road block'];
const STATUS_REASONS = {
    Ongoing: ['Driver started without app', 'App issue – trip running'],
    Completed: ['Completed offline', 'Passengers dropped – app not updated'],
    Cancelled: ['Client cancelled', 'No vehicle available', 'No passengers', 'Weather / road closure'],
    Scheduled: ['Rescheduled – driver confirmed'],
};

const svg = (children, size = 13, sw = 2.2) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const Icon = {
    alert: svg(<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>, 13, 2.5),
    phone: svg(<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />, 14),
    copy: svg(<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>),
    bell: svg(<><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>),
    swap: svg(<><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></>),
    edit: svg(<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></>),
    check: svg(<polyline points="20 6 9 17 4 12" />, 13, 2.5),
    bus: svg(<><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M3 11h18" /><circle cx="7.5" cy="19.5" r="1.5" /><circle cx="16.5" cy="19.5" r="1.5" /></>),
    refresh: svg(<><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>, 15, 2),
};

const ACTION_LABEL = {
    contacted: 'Driver contacted', nudged: 'Reminder pushed', reassigned: 'Driver reassigned',
    status_updated: 'Status updated', acknowledged: 'Acknowledged', escalated: 'Auto-escalated',
};

const timeAgo = (d) => {
    if (!d) return '';
    const m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    return `${Math.floor(m / 60)}h ${m % 60}m ago`;
};

const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';
const isToday = (d) => d && new Date(d).toDateString() === new Date().toDateString();

const CSS = `
@keyframes taPulse { 0% { box-shadow: 0 0 0 0 rgba(220,38,38,.45);} 70% { box-shadow: 0 0 0 9px rgba(220,38,38,0);} 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0);} }
@keyframes taSlide { from { transform: translateX(24px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
@keyframes taFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes taToast { from { transform: translateY(16px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
.ta-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 11px; border-radius:10px; font-size:12.5px; font-weight:700; cursor:pointer; border:1px solid #e2e8f0; background:#fff; color:#334155; font-family:'Outfit',sans-serif; transition:all .15s; text-decoration:none; white-space:nowrap; }
.ta-btn:hover { border-color:#cbd5e1; background:#f8fafc; }
.ta-btn.primary { background:#0f172a; color:#fff; border-color:#0f172a; }
.ta-btn.primary:hover { background:#1e293b; }
.ta-btn.call { background:#059669; color:#fff; border-color:#059669; }
.ta-btn.call:hover { background:#047857; }
.ta-btn.active { border-color:#6366f1; background:#eef2ff; color:#4338ca; }
.ta-btn:disabled { opacity:.55; cursor:not-allowed; }
.ta-input { width:100%; box-sizing:border-box; border:1px solid #e2e8f0; border-radius:10px; padding:9px 11px; font-size:13px; font-family:'Outfit',sans-serif; color:#0f172a; background:#fff; outline:none; }
.ta-input:focus { border-color:#6366f1; box-shadow:0 0 0 3px rgba(99,102,241,.12); }
.ta-chip { padding:5px 10px; border-radius:999px; border:1px solid #e2e8f0; background:#fff; font-size:12px; font-weight:600; color:#475569; cursor:pointer; font-family:'Outfit',sans-serif; }
.ta-chip:hover { border-color:#a5b4fc; color:#4338ca; }
`;

function AlertCard({ a, isSuperadmin, panel, form, setForm, busy, error, openPanel, submit, copy, drivers, vehicles }) {
    const s = SEV[a.severity];
    const isOpen = panel && panel.key === a.key;
    const opDrivers = (drivers[a.operatorId] || [])
        .filter(d => d._id !== a.driver?._id && (!d.status || d.status === 'Active'))
        .sort((x, y) => (isToday(x.lastLoginAt) ? 0 : 1) - (isToday(y.lastLoginAt) ? 0 : 1) || (x.name || '').localeCompare(y.name || ''));

    return (
        <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${a.acknowledged ? '#e2e8f0' : s.border}`, boxShadow: '0 1px 3px rgba(15,23,42,.05)', overflow: 'hidden', display: 'flex' }}>
            <div style={{ width: 4, background: s.solid, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: '14px 16px', minWidth: 0 }}>
                {/* Badges + lateness */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: s.fg, background: s.bg, border: `1px solid ${s.border}`, padding: '3px 8px', borderRadius: 999 }}>
                            {s.label}
                        </span>
                        {a.escalationLevel > 0 && (
                            <span style={{ fontSize: 10.5, fontWeight: 800, color: '#fff', background: a.escalationLevel === 2 ? '#7f1d1d' : '#9a3412', padding: '3px 8px', borderRadius: 999 }}>
                                ESCALATED L{a.escalationLevel}
                            </span>
                        )}
                        {a.acknowledged && (
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '3px 8px', borderRadius: 999 }}>Being handled</span>
                        )}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: s.fg, lineHeight: 1, whiteSpace: 'nowrap' }}>+{a.lateText}</div>
                </div>

                <div style={{ fontSize: 15.5, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.tripName}</div>
                <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
                    {a.headline} · Sched. {a.scheduledStart}{a.scheduledEnd ? ` – ${a.scheduledEnd}` : ''}
                    {a.clientName ? ` · ${a.clientName}` : ''}
                    {isSuperadmin && a.operatorName ? ` · ${a.operatorName}` : ''}
                </div>

                {/* Driver block */}
                <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 12, background: '#f8fafc', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 12, background: a.driver ? '#e0e7ff' : '#fee2e2', color: a.driver ? '#4338ca' : '#b91c1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                        {a.driver ? initials(a.driver.name) : '!'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{a.driver ? a.driver.name : 'No driver assigned'}</span>
                            {a.driver?.code && <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>#{a.driver.code}</span>}
                            {a.driver && (
                                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: a.driver.loggedInToday ? '#ecfdf5' : '#fff7ed', color: a.driver.loggedInToday ? '#047857' : '#c2410c' }}>
                                    {a.driver.loggedInToday ? 'Logged in' : 'Not logged in'}
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 13, color: '#334155', marginTop: 2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            {a.driver?.phone ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700 }}>
                                    {Icon.phone} {a.driver.phone}
                                    <button onClick={() => copy(a.driver.phone)} title="Copy number" style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2, display: 'flex' }}>{Icon.copy}</button>
                                </span>
                            ) : a.driver ? <span style={{ color: '#94a3b8' }}>No phone on file</span> : null}
                            {a.vehicle?.number && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#64748b' }}>{Icon.bus} {a.vehicle.number}</span>
                            )}
                        </div>
                    </div>
                    {a.driver?.phoneDial && (
                        <a className="ta-btn call" href={`tel:${a.driver.phoneDial}`} title={`Call ${a.driver.name}`}>{Icon.phone} Call</a>
                    )}
                </div>

                {a.lastAction && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                        <b style={{ color: '#334155' }}>{ACTION_LABEL[a.lastAction.action]}</b> by {a.lastAction.actedBy} · {timeAgo(a.lastAction.at)}
                        {a.lastAction.note ? <> — <i>{a.lastAction.note}</i></> : null}
                    </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                    {a.driver && (
                        <button className={`ta-btn ${isOpen && panel.type === 'contact' ? 'active' : ''}`} onClick={() => openPanel(a, 'contact')}>{Icon.check} Log contact</button>
                    )}
                    {a.driver?.hasApp && a.kind === 'not_started' && (
                        <button className="ta-btn" disabled={busy} onClick={() => submit(a, 'nudged', {})}>{Icon.bell} Nudge driver</button>
                    )}
                    {a.kind === 'not_started' && (
                        <button className={`ta-btn ${isOpen && panel.type === 'reassign' ? 'active' : ''}`} onClick={() => openPanel(a, 'reassign')}>{Icon.swap} {a.driver ? 'Replace driver' : 'Assign driver'}</button>
                    )}
                    <button className={`ta-btn ${isOpen && panel.type === 'status' ? 'active' : ''}`} onClick={() => openPanel(a, 'status')}>{Icon.edit} Update status</button>
                </div>

                {/* Inline forms */}
                {isOpen && (
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {panel.type === 'contact' && (
                            <>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>What did the driver say?</div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {QUICK_NOTES.map(n => <button key={n} className="ta-chip" onClick={() => setForm(f => ({ ...f, note: n }))}>{n}</button>)}
                                </div>
                                <input className="ta-input" placeholder="Note (e.g. reaching in 10 mins)" value={form.note || ''} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                                <button className="ta-btn primary" style={{ alignSelf: 'flex-end' }} disabled={busy} onClick={() => submit(a, 'contacted', { note: form.note })}>{busy ? 'Saving…' : 'Save'}</button>
                            </>
                        )}

                        {panel.type === 'reassign' && (
                            <>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Replacement driver</div>
                                <select className="ta-input" value={form.newDriverId || ''} onChange={e => setForm(f => ({ ...f, newDriverId: e.target.value }))}>
                                    <option value="">{drivers[a.operatorId] ? 'Select a driver…' : 'Loading drivers…'}</option>
                                    {opDrivers.map(d => (
                                        <option key={d._id} value={d._id}>
                                            {isToday(d.lastLoginAt) ? '🟢 ' : '⚪ '}{d.name}{d.phoneNumber ? ` · ${d.countryCode || ''} ${d.phoneNumber}` : ''}
                                        </option>
                                    ))}
                                </select>
                                <select className="ta-input" value={form.newVehicleId || ''} onChange={e => setForm(f => ({ ...f, newVehicleId: e.target.value }))}>
                                    <option value="">Keep current vehicle{a.vehicle?.number ? ` (${a.vehicle.number})` : ''}</option>
                                    {(vehicles[a.operatorId] || []).filter(v => v._id !== a.vehicle?._id).map(v => (
                                        <option key={v._id} value={v._id}>{v.plateNumber || v.registrationNumber || v.vehicleCode} {v.make || ''} {v.model || ''}</option>
                                    ))}
                                </select>
                                <input className="ta-input" placeholder="Reason (required) — e.g. original driver unreachable" value={form.reason || ''} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
                                <div style={{ fontSize: 11.5, color: '#64748b' }}>🟢 = logged in today. The new driver gets an urgent push notification.</div>
                                <button className="ta-btn primary" style={{ alignSelf: 'flex-end' }}
                                    disabled={busy || !form.newDriverId || !(form.reason || '').trim()}
                                    onClick={() => submit(a, 'reassigned', { newDriverId: form.newDriverId, newVehicleId: form.newVehicleId || undefined, reason: form.reason })}>
                                    {busy ? 'Assigning…' : 'Assign replacement'}
                                </button>
                            </>
                        )}

                        {panel.type === 'status' && (
                            <>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>New status</div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {['Ongoing', 'Completed', 'Cancelled', 'Scheduled'].filter(st => st !== a.status).map(st => (
                                        <button key={st} className={`ta-btn ${form.newStatus === st ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, newStatus: st, reason: '' }))}>{st}</button>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {(STATUS_REASONS[form.newStatus] || []).map(r => <button key={r} className="ta-chip" onClick={() => setForm(f => ({ ...f, reason: r }))}>{r}</button>)}
                                </div>
                                <input className="ta-input" placeholder="Reason (required)" value={form.reason || ''} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
                                <button className="ta-btn primary" style={{ alignSelf: 'flex-end' }}
                                    disabled={busy || !form.newStatus || !(form.reason || '').trim()}
                                    onClick={() => submit(a, 'status_updated', { newStatus: form.newStatus, reason: form.reason })}>
                                    {busy ? 'Updating…' : `Mark as ${form.newStatus}`}
                                </button>
                            </>
                        )}

                        {error && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600 }}>{error}</div>}
                    </div>
                )}

                {/* Activity / escalation trail */}
                {a.actions && a.actions.length > 0 && (
                    <details style={{ marginTop: 10 }}>
                        <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>Activity ({a.actions.length})</summary>
                        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {a.actions.map((l, i) => (
                                <div key={i} style={{ fontSize: 12, color: '#475569' }}>
                                    <span style={{ color: '#94a3b8' }}>{new Date(l.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                                    {' · '}<b>{ACTION_LABEL[l.action]}{l.action === 'escalated' ? ` L${l.escalationLevel}` : ''}</b>
                                    {' · '}{l.actedBy}{(l.reason || l.note) && l.action !== 'escalated' ? ` — ${l.reason || l.note}` : ''}
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </div>
        </div>
    );
}

export default function TripAlertsCenter({ operatorId, isSuperadmin, user }) {
    const [data, setData] = useState({ total: 0, critical: 0, overdue: 0, delayed: 0, unacknowledged: 0, alerts: [] });
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('all');
    const [panel, setPanel] = useState(null); // { key, type: 'contact' | 'reassign' | 'status' }
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [toast, setToast] = useState(null);
    const [drivers, setDrivers] = useState({});   // operatorId -> list
    const [vehicles, setVehicles] = useState({}); // operatorId -> list
    const seenEscalations = useRef(null);

    const op = isSuperadmin ? 'all' : operatorId;
    const actedBy = user?.name || user?.fullName || user?.companyName || user?.email || 'Operator';

    const load = useCallback(async () => {
        if (!op) return;
        try {
            const res = await fetch(`/api/trip-alerts/${op}`);
            if (!res.ok) return;
            const json = await res.json();
            if (!json || !Array.isArray(json.alerts)) return;
            setData(json);

            // Toast when a trip newly reaches Overdue / Critical
            const current = new Set(json.alerts.filter(a => a.escalationLevel >= 1).map(a => `${a.key}:${a.escalationLevel}`));
            if (seenEscalations.current) {
                const fresh = json.alerts.find(a => a.escalationLevel >= 1 && !seenEscalations.current.has(`${a.key}:${a.escalationLevel}`));
                if (fresh) setToast(fresh);
            } else {
                const firstCritical = json.alerts.find(a => a.severity === 'critical' && !a.acknowledged);
                if (firstCritical) setToast(firstCritical);
            }
            seenEscalations.current = current;
        } catch (e) { /* ignore */ }
    }, [op]);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(null), 15000);
        return () => clearTimeout(t);
    }, [toast]);

    const ensureFleet = async (opId) => {
        if (!opId || drivers[opId]) return;
        const [d, v] = await Promise.all([
            fetch(`/api/drivers/${opId}`).then(r => r.json()).catch(() => []),
            fetch(`/api/vehicles/${opId}`).then(r => r.json()).catch(() => []),
        ]);
        setDrivers(prev => ({ ...prev, [opId]: Array.isArray(d) ? d : [] }));
        setVehicles(prev => ({ ...prev, [opId]: Array.isArray(v) ? v : [] }));
    };

    const openPanel = (alert, type) => {
        setError('');
        if (panel && panel.key === alert.key && panel.type === type) { setPanel(null); return; }
        setPanel({ key: alert.key, type });
        setForm(type === 'status'
            ? { newStatus: alert.kind === 'overrunning' ? 'Completed' : 'Cancelled', reason: '' }
            : { reason: '', note: '' });
        if (type === 'reassign') ensureFleet(alert.operatorId);
    };

    const showFlash = (msg, ms = 3500) => { setFlash(msg); setTimeout(() => setFlash(''), ms); };

    const submit = async (alert, action, extra = {}) => {
        setBusy(true); setError('');
        try {
            const res = await fetch('/api/trip-alerts/action', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tripId: alert.tripId, instanceId: alert.instanceId, action, actedBy, ...extra }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json.message || 'Action failed');
            setPanel(null);
            showFlash(`${ACTION_LABEL[action] || 'Saved'} · ${alert.tripName}`);
            await load();
        } catch (e) {
            if (!panel || panel.key !== alert.key) showFlash(e.message, 5000);
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const copy = (txt) => {
        try { navigator.clipboard.writeText(txt); showFlash(`Copied ${txt}`, 2000); } catch (e) { /* ignore */ }
    };

    if (!op) return null;

    const list = data.alerts.filter(a => filter === 'all' || a.severity === filter);
    const hasCritical = data.critical > 0;
    const topSev = hasCritical ? SEV.critical : data.overdue > 0 ? SEV.overdue : SEV.delayed;
    const tSev = toast ? SEV[toast.severity] : null;

    return (
        <>
            <style>{CSS}</style>

            {/* ── Header pill ─────────────────────────────── */}
            {data.total > 0 && (
                <button
                    onClick={() => setOpen(true)}
                    title="Delayed & overdue trips"
                    style={{
                        marginRight: 10,
                        background: hasCritical ? '#dc2626' : topSev.bg,
                        color: hasCritical ? '#ffffff' : topSev.fg,
                        border: `1px solid ${hasCritical ? '#dc2626' : topSev.border}`,
                        padding: '6px 13px 6px 9px', borderRadius: 30, fontSize: 12.5, fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
                        fontFamily: "'Outfit', sans-serif",
                        animation: hasCritical ? 'taPulse 1.6s infinite' : 'none',
                    }}
                >
                    <span style={{
                        width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: hasCritical ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.7)',
                    }}>{Icon.alert}</span>
                    <span>{hasCritical || data.overdue > 0 ? 'Trips Overdue' : 'Trips Delayed'}</span>
                    <span style={{
                        background: hasCritical ? '#ffffff' : topSev.solid, color: hasCritical ? '#dc2626' : '#ffffff',
                        fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 12, lineHeight: 1,
                    }}>{data.total}</span>
                </button>
            )}

            {/* ── Toast for new escalations ────────────────── */}
            {toast && !open && (
                <div style={{
                    position: 'fixed', right: 24, bottom: 24, zIndex: 10001, width: 'min(380px, calc(100vw - 32px))', background: '#fff',
                    borderRadius: 16, boxShadow: '0 20px 50px rgba(15,23,42,.22)', border: `1px solid ${tSev.border}`,
                    overflow: 'hidden', animation: 'taToast .25s ease-out', fontFamily: "'Outfit', sans-serif", textAlign: 'left',
                }}>
                    <div style={{ height: 4, background: tSev.solid }} />
                    <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', color: tSev.fg, textTransform: 'uppercase' }}>
                                {toast.escalationLevel === 2 ? 'Escalated · ' : ''}{tSev.label} · {toast.lateText} late
                            </span>
                            <button onClick={() => setToast(null)} style={{ border: 'none', background: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>{toast.tripName}</div>
                        <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>{toast.headline}</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <div style={{ fontSize: 13, color: '#334155', minWidth: 0 }}>
                                <b>{toast.driver?.name || 'No driver'}</b>
                                {toast.driver?.phone && <span style={{ color: '#64748b' }}> · {toast.driver.phone}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                {toast.driver?.phoneDial && <a className="ta-btn call" href={`tel:${toast.driver.phoneDial}`} title="Call driver">{Icon.phone}</a>}
                                <button className="ta-btn primary" onClick={() => { setOpen(true); setToast(null); }}>View</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Slide-in panel ──────────────────────────── */}
            {open && (
                <div
                    onClick={() => setOpen(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', backdropFilter: 'blur(3px)', zIndex: 10000, animation: 'taFade .15s', fontFamily: "'Outfit', sans-serif", textAlign: 'left' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)', background: '#f8fafc',
                            display: 'flex', flexDirection: 'column', boxShadow: '-20px 0 50px rgba(15,23,42,.18)', animation: 'taSlide .2s ease-out',
                        }}
                    >
                        <div style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)', color: '#fff', padding: '20px 22px 16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em', color: '#fca5a5', marginBottom: 4 }}>OPERATIONS ALERTS</div>
                                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#fff' }}>Delayed & Overdue Trips</h2>
                                    <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#94a3b8' }}>
                                        {data.unacknowledged} need attention · auto-refreshes every 30s
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button onClick={load} title="Refresh" style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)', color: '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Icon.refresh}</button>
                                    <button onClick={() => setOpen(false)} style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.06)', color: '#cbd5e1', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>&times;</button>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                                {[['all', 'All', data.total, null], ['critical', 'Critical', data.critical, '#f87171'], ['overdue', 'Overdue', data.overdue, '#fb923c'], ['delayed', 'Delayed', data.delayed, '#fbbf24']].map(([k, label, n, dot]) => (
                                    <button key={k} onClick={() => setFilter(k)} style={{
                                        display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                                        fontSize: 12.5, fontWeight: 700, fontFamily: "'Outfit', sans-serif",
                                        background: filter === k ? '#fff' : 'rgba(255,255,255,.06)', color: filter === k ? '#0f172a' : '#cbd5e1',
                                        border: `1px solid ${filter === k ? '#fff' : 'rgba(255,255,255,.14)'}`,
                                    }}>
                                        {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot }} />}
                                        {label} <span style={{ opacity: .7 }}>{n}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {flash && (
                            <div style={{ margin: '12px 18px 0', padding: '9px 12px', borderRadius: 10, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: 13, fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}>
                                {Icon.check} {flash}
                            </div>
                        )}

                        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {list.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
                                    <div style={{ width: 52, height: 52, borderRadius: 16, background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                                        {svg(<polyline points="20 6 9 17 4 12" />, 24, 2.5)}
                                    </div>
                                    <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>All trips on time</div>
                                    <div style={{ fontSize: 13, marginTop: 4 }}>No {filter !== 'all' ? `${filter} ` : ''}trips right now.</div>
                                </div>
                            )}
                            {list.map(a => (
                                <AlertCard
                                    key={a.key}
                                    a={a}
                                    isSuperadmin={isSuperadmin}
                                    panel={panel}
                                    form={form}
                                    setForm={setForm}
                                    busy={busy}
                                    error={error}
                                    openPanel={openPanel}
                                    submit={submit}
                                    copy={copy}
                                    drivers={drivers}
                                    vehicles={vehicles}
                                />
                            ))}
                        </div>

                        <div style={{ padding: '10px 18px', borderTop: '1px solid #e2e8f0', background: '#fff', fontSize: 11.5, color: '#64748b' }}>
                            Delayed after 1 min · Overdue at 10 min (operator notified + driver reminded) · Critical at 30 min (escalated)
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
